import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, ButtonGroup, Form, Modal, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import styled from "styled-components";
import constants from "../../const";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import AppsIcon from "@mui/icons-material/Apps";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FloatingVideoPlayer from "./FloatingVideoPlayer";

const API_BASE = `${constants.CONTENT_DOMAIN}file-browser`;

const getToken = () => localStorage.getItem("token").slice(7);

const mediaUrl = (relPath) =>
    `${API_BASE}/media/${relPath.split("/").map(encodeURIComponent).join("/")}?token=${getToken()}`;

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit++;
    }
    return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
};

const errorNotice = (err, fallback) => ({
    variant: "danger",
    text: err.response?.data?.message || err.message || fallback
});

const summarize = (label, summary, failed) => {
    const text = `${label}: ${summary.success} of ${summary.total} succeeded.`;
    const reasons = failed.map((f) => `${f.path} (${f.reason})`).join("; ");
    return {
        variant: summary.failed ? "warning" : "success",
        text: reasons ? `${text} ${reasons}` : text
    };
};

const TileGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${(p) => p.$min}px, 1fr));
    gap: 8px;
`;

const Tile = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    cursor: pointer;
    border-radius: 6px;
    border: 3px solid ${(p) => (p.$selected ? "#0d6efd" : "transparent")};
    background: #1e1e1e;
    color: #eee;
`;

const Preview = styled.div`
    position: relative;
    height: ${(p) => p.$height}px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #111;
    overflow: hidden;

    img,
    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const Caption = styled.div`
    padding: 4px 6px;
    font-size: ${(p) => (p.$small ? "11px" : "12px")};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TileCheck = styled.input`
    position: absolute;
    top: 6px;
    left: 6px;
    pointer-events: none;
    margin: 0;
`;

const OpenButton = styled.button`
    position: absolute;
    top: 4px;
    right: 4px;
    border: none;
    border-radius: 4px;
    padding: 2px 4px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
`;

const PlayButton = styled.button`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: none;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
`;

const FloatingStack = styled.div`
    position: fixed;
    right: 20px;
    bottom: 92px;
    z-index: 997;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`;

const FloatingActionButton = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid ${(p) => (p.$danger ? "#dc3545" : "#667eea")};
    background: white;
    color: ${(p) => (p.$danger ? "#dc3545" : "#667eea")};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
        transform: scale(1.1);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const FileBrowser = () => {
    const [currentPath, setCurrentPath] = useState("");
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [viewMode, setViewMode] = useState("list");
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [showMove, setShowMove] = useState(false);
    const [folderOptions, setFolderOptions] = useState([]);
    const [moveTarget, setMoveTarget] = useState("");
    const [showRename, setShowRename] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [renameError, setRenameError] = useState(null);
    const [playing, setPlaying] = useState(null);
    const [floatingCollapsed, setFloatingCollapsed] = useState(false);

    const loadFolder = useCallback(async (path) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_BASE}/list`, { params: { path, token: getToken() } });
            setCurrentPath(data.path);
            setFolders(data.folders);
            setFiles(data.files);
            setSelected(new Set());
        } catch (err) {
            setNotice(errorNotice(err, "Failed to load folder"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFolder("");
    }, [loadFolder]);

    const selectedPaths = [...selected];
    const allSelected = files.length > 0 && files.every((f) => selected.has(f.path));
    const selectedFile = selectedPaths.length === 1 ? files.find((f) => f.path === selectedPaths[0]) || null : null;
    const selectedVideo = selectedFile && selectedFile.type === "video" ? selectedFile : null;
    const crumbs = currentPath ? currentPath.split("/") : [];
    const crumbPath = (index) => crumbs.slice(0, index + 1).join("/");

    const toggleSelect = (path) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            } else {
                next.add(path);
            }
            return next;
        });
    };

    const toggleAll = () => {
        setSelected(allSelected ? new Set() : new Set(files.map((f) => f.path)));
    };

    const runOperation = async (endpoint, body, label) => {
        setBusy(true);
        setNotice(null);
        try {
            const { data } = await axios.post(`${API_BASE}/${endpoint}`, body, { params: { token: getToken() } });
            setNotice(summarize(label, data.summary, data.failed || []));
            if (endpoint !== "duplicate" && playing && (body.paths || []).includes(playing.path)) {
                setPlaying(null);
            }
            await loadFolder(currentPath);
        } catch (err) {
            setNotice(errorNotice(err, `${label} failed`));
        } finally {
            setBusy(false);
        }
    };

    const handleDuplicate = () => runOperation("duplicate", { paths: selectedPaths }, "Duplicate");

    const handleDelete = () => {
        if (!window.confirm(`Permanently delete ${selectedPaths.length} file(s)? This cannot be undone.`)) return;
        runOperation("delete", { paths: selectedPaths }, "Delete");
    };

    const openMove = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/folders`, { params: { token: getToken() } });
            setFolderOptions(data.folders);
            const firstOther = data.folders.find((f) => f !== currentPath);
            setMoveTarget(firstOther !== undefined ? firstOther : "");
            setShowMove(true);
        } catch (err) {
            setNotice(errorNotice(err, "Failed to load folders"));
        }
    };

    const confirmMove = () => {
        setShowMove(false);
        runOperation("move", { paths: selectedPaths, targetPath: moveTarget }, "Move");
    };

    const openRename = () => {
        if (!selectedFile) return;
        setRenameValue(selectedFile.name);
        setRenameError(null);
        setShowRename(true);
    };

    const confirmRename = async () => {
        if (!selectedFile) return;
        const newName = renameValue.trim();
        setBusy(true);
        setRenameError(null);
        try {
            const { data } = await axios.post(
                `${API_BASE}/rename`,
                { path: selectedFile.path, newName },
                { params: { token: getToken() } }
            );
            setPlaying((prev) => (prev && prev.path === selectedFile.path ? { ...prev, path: data.to, name: newName } : prev));
            setShowRename(false);
            setNotice({ variant: "success", text: `Renamed to "${newName}"` });
            await loadFolder(currentPath);
        } catch (err) {
            setRenameError(errorNotice(err, "Rename failed").text);
        } finally {
            setBusy(false);
        }
    };

    const openFile = (e, path) => {
        e.stopPropagation();
        window.open(mediaUrl(path), "_blank", "noopener");
    };

    const renderFilePreview = (file) => {
        if (file.type === "image") {
            return <img src={mediaUrl(file.path)} alt={file.name} loading="lazy" />;
        }
        if (file.type === "video") {
            return <video src={mediaUrl(file.path)} preload="metadata" muted />;
        }
        return <InsertDriveFileIcon style={{ fontSize: 48, color: "#888" }} />;
    };

    const renderTiles = (small) => {
        const size = small ? 110 : 250;
        return (
            <TileGrid $min={size}>
                {folders.map((folder) => (
                    <Tile key={`folder-${folder.path}`} $selected={false} onClick={() => loadFolder(folder.path)}>
                        <Preview $height={size}>
                            <FolderIcon style={{ fontSize: small ? 40 : 72, color: "#f0c040" }} />
                        </Preview>
                        <Caption $small={small}>{folder.name}</Caption>
                    </Tile>
                ))}
                {files.map((file) => (
                    <Tile key={file.path} $selected={selected.has(file.path)} onClick={() => toggleSelect(file.path)} title={file.name}>
                        <TileCheck type="checkbox" checked={selected.has(file.path)} readOnly />
                        <OpenButton type="button" title="Open in new tab" onClick={(e) => openFile(e, file.path)}>
                            <OpenInNewIcon style={{ fontSize: 16 }} />
                        </OpenButton>
                        <Preview $height={size}>
                            {renderFilePreview(file)}
                            {file.type === "video" && (
                                <PlayButton
                                    type="button"
                                    title="Play"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPlaying(file);
                                    }}
                                >
                                    <PlayArrowIcon />
                                </PlayButton>
                            )}
                        </Preview>
                        <Caption $small={small}>{file.name}</Caption>
                        {!small && <Caption $small>{formatSize(file.size)}</Caption>}
                    </Tile>
                ))}
            </TileGrid>
        );
    };

    const renderList = () => (
        <Table hover size="sm" responsive className="align-middle">
            <thead>
                <tr>
                    <th style={{ width: 36 }} />
                    <th>Name</th>
                    <th style={{ width: 110 }}>Size</th>
                    <th style={{ width: 190 }}>Modified</th>
                    <th style={{ width: 90 }}>Type</th>
                </tr>
            </thead>
            <tbody>
                {folders.map((folder) => (
                    <tr key={`folder-${folder.path}`} style={{ cursor: "pointer" }} onClick={() => loadFolder(folder.path)}>
                        <td />
                        <td><FolderIcon fontSize="small" className="me-1" style={{ color: "#f0c040" }} />{folder.name}</td>
                        <td>—</td>
                        <td>—</td>
                        <td>Folder</td>
                    </tr>
                ))}
                {files.map((file) => (
                    <tr key={file.path} style={{ cursor: "pointer" }} onClick={() => toggleSelect(file.path)}>
                        <td>
                            <Form.Check
                                checked={selected.has(file.path)}
                                onChange={() => toggleSelect(file.path)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </td>
                        <td>
                            <InsertDriveFileIcon fontSize="small" className="me-1" />
                            <a
                                href={mediaUrl(file.path)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {file.name}
                            </a>
                            {file.type === "video" && (
                                <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 ms-2 align-baseline"
                                    title="Play"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPlaying(file);
                                    }}
                                >
                                    <PlayArrowIcon fontSize="small" />
                                </Button>
                            )}
                        </td>
                        <td>{formatSize(file.size)}</td>
                        <td>{new Date(file.modified).toLocaleString()}</td>
                        <td>{file.type}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );

    const isEmpty = !loading && folders.length === 0 && files.length === 0;

    return (
        <div className="p-3">
            <h4 className="mb-2">File Browser</h4>

            <div className="d-flex align-items-center flex-wrap gap-1 mb-2">
                <Button size="sm" variant="outline-secondary" title="Root" onClick={() => loadFolder("")}>
                    <HomeIcon fontSize="small" />
                </Button>
                {crumbs.map((name, i) => (
                    <React.Fragment key={crumbPath(i)}>
                        <span>/</span>
                        <Button size="sm" variant="link" onClick={() => loadFolder(crumbPath(i))}>{name}</Button>
                    </React.Fragment>
                ))}
                {loading && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <ButtonGroup size="sm">
                    <Button variant={viewMode === "list" ? "primary" : "outline-secondary"} title="List" onClick={() => setViewMode("list")}>
                        <ViewListIcon fontSize="small" />
                    </Button>
                    <Button variant={viewMode === "gallery" ? "primary" : "outline-secondary"} title="Gallery" onClick={() => setViewMode("gallery")}>
                        <ViewModuleIcon fontSize="small" />
                    </Button>
                    <Button variant={viewMode === "thumbnails" ? "primary" : "outline-secondary"} title="Thumbnails" onClick={() => setViewMode("thumbnails")}>
                        <AppsIcon fontSize="small" />
                    </Button>
                </ButtonGroup>

                <Button size="sm" variant="outline-secondary" title="Refresh" onClick={() => loadFolder(currentPath)} disabled={loading || busy}>
                    <RefreshIcon fontSize="small" />
                </Button>

                <Form.Check
                    inline
                    type="checkbox"
                    label="All files"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={files.length === 0}
                />

                <span className="text-muted small">{selected.size} selected</span>
            </div>

            {notice && (
                <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)}>
                    {notice.text}
                </Alert>
            )}

            {isEmpty && <p className="text-muted">This folder is empty.</p>}

            {viewMode === "list" && renderList()}
            {viewMode === "gallery" && renderTiles(false)}
            {viewMode === "thumbnails" && renderTiles(true)}

            <Modal show={showMove} onHide={() => setShowMove(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Move {selected.size} file(s)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Label>Destination folder</Form.Label>
                    <Form.Select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}>
                        {folderOptions.map((path) => (
                            <option key={path} value={path}>{path === "" ? "/ (root)" : path}</option>
                        ))}
                    </Form.Select>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMove(false)}>Cancel</Button>
                    <Button variant="primary" onClick={confirmMove} disabled={busy}>Move</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showRename} onHide={() => setShowRename(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Rename file</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={(e) => { e.preventDefault(); confirmRename(); }}>
                        <Form.Label>New name</Form.Label>
                        <Form.Control value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus />
                        <Form.Text className="text-muted">Keep the extension so the file still opens.</Form.Text>
                        {renameError && <Alert variant="danger" className="mt-2 mb-0">{renameError}</Alert>}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRename(false)}>Cancel</Button>
                    <Button variant="primary" onClick={confirmRename} disabled={busy || !renameValue.trim()}>Rename</Button>
                </Modal.Footer>
            </Modal>

            <FloatingStack>
                {!floatingCollapsed && (
                <>
                <FloatingActionButton
                    type="button"
                    title="Play selected video"
                    aria-label="Play selected video"
                    disabled={!selectedVideo}
                    onClick={() => setPlaying(selectedVideo)}
                >
                    <PlayArrowIcon />
                </FloatingActionButton>
                <FloatingActionButton
                    type="button"
                    title="Rename selected file"
                    aria-label="Rename selected file"
                    disabled={!selectedFile || busy}
                    onClick={openRename}
                >
                    <EditIcon />
                </FloatingActionButton>
                <FloatingActionButton
                    type="button"
                    title="Move selected"
                    aria-label="Move selected"
                    disabled={selected.size === 0 || busy}
                    onClick={openMove}
                >
                    <DriveFileMoveIcon />
                </FloatingActionButton>
                <FloatingActionButton
                    type="button"
                    title="Duplicate selected"
                    aria-label="Duplicate selected"
                    disabled={selected.size === 0 || busy}
                    onClick={handleDuplicate}
                >
                    <ContentCopyIcon />
                </FloatingActionButton>
                <FloatingActionButton
                    type="button"
                    $danger
                    title="Delete selected"
                    aria-label="Delete selected"
                    disabled={selected.size === 0 || busy}
                    onClick={handleDelete}
                >
                    <DeleteIcon />
                </FloatingActionButton>
                </>
                )}
                <FloatingActionButton
                    type="button"
                    onClick={() => setFloatingCollapsed(prev => !prev)}
                    title={floatingCollapsed ? "Show file actions" : "Hide file actions"}
                    aria-label={floatingCollapsed ? "Show file actions" : "Hide file actions"}
                    aria-expanded={!floatingCollapsed}
                >
                    {floatingCollapsed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </FloatingActionButton>
            </FloatingStack>

            {playing && (
                <FloatingVideoPlayer
                    name={playing.name}
                    src={mediaUrl(playing.path)}
                    onClose={() => setPlaying(null)}
                />
            )}
        </div>
    );
};

export default FileBrowser;
